import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Check, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { workflowAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const KIND_STYLES = {
  assigned: 'bg-blue-500/15 text-blue-600',
  reminder: 'bg-amber-500/15 text-amber-600',
  overdue: 'bg-red-500/15 text-red-600',
  escalated: 'bg-purple-500/15 text-purple-600',
  decided: 'bg-green-500/15 text-green-600',
};

const WorkflowInbox = () => {
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await workflowAPI.listNotifications();
      setRows(res?.data?.notifications || []);
      setUnread(res?.data?.unread || 0);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load notifications'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await workflowAPI.markNotificationRead(id);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not mark read'));
    }
  };

  return (
    <Card className="bg-card border-border animate-fade-in">
      <CardHeader className="border-b border-border/50 bg-muted/20">
        <CardTitle className="text-foreground flex items-center gap-2 text-base">
          <Bell className="h-4 w-4" /> Notifications
          {unread > 0 && <Badge className="bg-red-500/15 text-red-600">{unread} unread</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No notifications yet.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((n) => (
              <div key={n.id} className={`flex items-start justify-between gap-3 rounded-lg border border-border p-3 ${n.read ? 'opacity-60' : ''}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className={KIND_STYLES[n.kind] || 'bg-muted text-muted-foreground'}>{n.kind}</Badge>
                    <p className="text-sm font-medium text-foreground truncate">{n.subject}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                </div>
                {!n.read && (
                  <Button size="sm" variant="ghost" onClick={() => markRead(n.id)} title="Mark read"><Check className="h-4 w-4" /></Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkflowInbox;
